const ACCESS_LEVELS = ["none", "view", "act", "admin"];
const LEGACY_ROLE_MAP = {
  client_admin: "facility_admin",
  client_manager: "facility_manager",
  client_worker: "facility_worker",
};

export const CLIENT_PORTAL_MODULES = {
  dashboard: "dashboard",
  billing: "billing",
  team_management: "team_management",
  organization_settings: "organization_settings",
  internal_access_policy: "internal_access_policy",
  facility_internal_access_policy: "facility_internal_access_policy",
  service_change: "service_change",
  consulting_workspace: "consulting_workspace",
  consulting_document_approval: "consulting_document_approval",
  lms_management: "lms_management",
  staffing_requests: "staffing_requests",
  staffing_rate_approvals: "staffing_rate_approvals",
  staffing_timesheet_approvals: "staffing_timesheet_approvals",
  facility_billing: "facility_billing",
  messages: "messages",
};

const CLIENT_PORTAL_ROLES = [
  "org_admin",
  "org_team",
  "facility_admin",
  "facility_manager",
  "facility_worker",
  "worker",
];

function normalizeRole(role) {
  const normalized = String(role || "").trim();
  return LEGACY_ROLE_MAP[normalized] || normalized;
}

const DEFAULT_CLIENT_PORTAL_ACCESS = {
  dashboard: {
    org_admin: "admin",
    org_team: "view",
    facility_admin: "view",
    facility_manager: "view",
    facility_worker: "view",
    worker: "view",
  },
  billing: {
    org_admin: "admin",
    org_team: "none",
    facility_admin: "admin",
    facility_manager: "none",
    facility_worker: "none",
    worker: "none",
  },
  team_management: {
    org_admin: "admin",
    org_team: "none",
    facility_admin: "admin",
    facility_manager: "none",
    facility_worker: "none",
    worker: "none",
  },
  organization_settings: {
    org_admin: "admin",
    org_team: "none",
    facility_admin: "view",
    facility_manager: "none",
    facility_worker: "none",
    worker: "none",
  },
  internal_access_policy: {
    org_admin: "admin",
    org_team: "none",
    facility_admin: "none",
    facility_manager: "none",
    facility_worker: "none",
    worker: "none",
  },
  facility_internal_access_policy: {
    org_admin: "admin",
    org_team: "view",
    facility_admin: "act",
    facility_manager: "none",
    facility_worker: "none",
    worker: "none",
  },
  service_change: {
    org_admin: "admin",
    org_team: "none",
    facility_admin: "admin",
    facility_manager: "none",
    facility_worker: "none",
    worker: "none",
  },
  consulting_workspace: {
    org_admin: "admin",
    org_team: "act",
    facility_admin: "act",
    facility_manager: "act",
    facility_worker: "none",
    worker: "none",
  },
  consulting_document_approval: {
    org_admin: "admin",
    org_team: "act",
    facility_admin: "act",
    facility_manager: "act",
    facility_worker: "none",
    worker: "none",
  },
  lms_management: {
    org_admin: "admin",
    org_team: "act",
    facility_admin: "act",
    facility_manager: "act",
    facility_worker: "none",
    worker: "none",
  },
  staffing_requests: {
    org_admin: "admin",
    org_team: "act",
    facility_admin: "act",
    facility_manager: "act",
    facility_worker: "none",
    worker: "none",
  },
  staffing_rate_approvals: {
    org_admin: "admin",
    org_team: "none",
    facility_admin: "act",
    facility_manager: "none",
    facility_worker: "none",
    worker: "none",
  },
  staffing_timesheet_approvals: {
    org_admin: "none",
    org_team: "act",
    facility_admin: "act",
    facility_manager: "act",
    facility_worker: "none",
    worker: "none",
  },
  facility_billing: {
    org_admin: "admin",
    org_team: "view",
    facility_admin: "none",
    facility_manager: "none",
    facility_worker: "none",
    worker: "none",
  },
  messages: {
    org_admin: "admin",
    org_team: "act",
    facility_admin: "act",
    facility_manager: "act",
    facility_worker: "act",
    worker: "none",
  },
};

function normalizeAccessLevel(level) {
  const normalized = String(level || "none")
    .trim()
    .toLowerCase();
  return ACCESS_LEVELS.includes(normalized) ? normalized : "none";
}

function buildClientPortalAccessMap(overrides = {}) {
  const output = {};
  for (const moduleKey of Object.keys(DEFAULT_CLIENT_PORTAL_ACCESS)) {
    output[moduleKey] = {
      ...DEFAULT_CLIENT_PORTAL_ACCESS[moduleKey],
    };
    const moduleOverrides = overrides?.[moduleKey];
    if (!moduleOverrides || typeof moduleOverrides !== "object") continue;
    for (const role of CLIENT_PORTAL_ROLES) {
      const overrideKey = moduleOverrides[role] !== undefined
        ? role
        : Object.keys(moduleOverrides).find((key) => normalizeRole(key) === role);
      if (!overrideKey) continue;
      output[moduleKey][role] = normalizeAccessLevel(moduleOverrides[overrideKey]);
    }
  }
  return output;
}

function getClientPortalAccessLevel({ role, moduleKey, overrides } = {}) {
  const accessMap = buildClientPortalAccessMap(overrides);
  return accessMap?.[moduleKey]?.[normalizeRole(role)] || "none";
}

function hasClientPortalAccess({
  role,
  moduleKey,
  requiredLevel = "view",
  overrides,
} = {}) {
  const rank = { none: 0, view: 1, act: 2, admin: 3 };
  return (
    (rank[getClientPortalAccessLevel({ role, moduleKey, overrides })] || 0) >=
    (rank[requiredLevel] || 0)
  );
}

export function getClientPortalPermissionMap(user) {
  if (
    user?.clientPortalPermissions &&
    typeof user.clientPortalPermissions === "object"
  ) {
    return user.clientPortalPermissions;
  }
  const overrides = user?.organizationId?.portalAccessOverrides || {};
  return buildClientPortalAccessMap(overrides);
}

export function hasUserModuleAccess(user, moduleKey, requiredLevel = "view") {
  const permissionMap = getClientPortalPermissionMap(user);
  const level = permissionMap?.[moduleKey]?.[normalizeRole(user?.role)] || "none";
  const rank = { none: 0, view: 1, act: 2, admin: 3 };
  return (rank[level] || 0) >= (rank[requiredLevel] || 0);
}

export function hasUserModuleAccessFromOverrides(
  user,
  moduleKey,
  requiredLevel = "view",
) {
  return hasClientPortalAccess({
    role: user?.role,
    moduleKey,
    requiredLevel,
    overrides: user?.organizationId?.portalAccessOverrides || {},
  });
}

export function canAccessBillingWorkspace(user) {
  if (!user || !["org_admin", "facility_admin"].includes(normalizeRole(user.role))) {
    return false;
  }
  return hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.billing, "view");
}
